"use client";
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useAnimation, useMotionValue } from 'framer-motion';
import { createPortal } from 'react-dom';
import { usePromo } from './context/PromoContext';

const PromoRibbon = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [toast, setToast] = useState(null); // { message, visible }
  const [isClient, setIsClient] = useState(false);

  const controls = useAnimation();
  const x = useMotionValue(0);
  const startTimeRef = useRef(null);
  const pausedProgressRef = useRef(0);

  const { promoCodes, promoCodesLoading, hasPromoCodes, promoCodesError } = usePromo();

  // Ensure we're on the client side for portal
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Debug effect
  useEffect(() => {
    console.log("PromoRibbon state:", {
      promoCodesCount: promoCodes.length,
      promoCodesLoading,
      hasPromoCodes,
      promoCodesError,
      promoCodes: promoCodes.slice(0, 2)
    });
  }, [promoCodes, promoCodesLoading, hasPromoCodes, promoCodesError]);

  // Animation setup
  useEffect(() => {
    if (!hasPromoCodes || promoCodes.length === 0) return;

    const itemWidth = 250;
    const totalWidth = promoCodes.length * itemWidth;
    const duration = promoCodes.length * 3;

    const startAnimation = (fromProgress = 0) => {
      const remainingDuration = duration * (1 - fromProgress);
      const startPosition = -totalWidth * fromProgress;

      x.set(startPosition);

      controls.start({
        x: -totalWidth,
        transition: {
          duration: remainingDuration,
          ease: "linear",
          repeat: Infinity,
          repeatType: "loop"
        }
      });

      startTimeRef.current = Date.now() - (duration * fromProgress * 1000);
    };

    if (!isHovered) {
      startAnimation(pausedProgressRef.current);
    }

    return () => {
      controls.stop();
    };
  }, [hasPromoCodes, promoCodes.length, isHovered, controls, x]);

  // Handle hover state changes
  useEffect(() => {
    if (!hasPromoCodes || promoCodes.length === 0) return;

    const itemWidth = 250;
    const totalWidth = promoCodes.length * itemWidth;
    const duration = promoCodes.length * 3;

    if (isHovered) {
      // Pause animation and calculate current progress
      controls.stop();

      if (startTimeRef.current) {
        const elapsed = (Date.now() - startTimeRef.current) / 1000;
        const cycleProgress = (elapsed % duration) / duration;
        pausedProgressRef.current = cycleProgress;
      }
    } else {
      // Resume animation from paused position
      const startAnimation = (fromProgress = 0) => {
        const remainingDuration = duration * (1 - fromProgress);
        const startPosition = -totalWidth * fromProgress;

        x.set(startPosition);

        controls.start({
          x: -totalWidth,
          transition: {
            duration: remainingDuration,
            ease: "linear",
            repeat: Infinity,
            repeatType: "loop"
          }
        });

        startTimeRef.current = Date.now() - (duration * fromProgress * 1000);
      };

      startAnimation(pausedProgressRef.current);
    }
  }, [isHovered, hasPromoCodes, promoCodes.length, controls, x]);

  // Copy to clipboard + show toast
  const handlePromoClick = async (promo) => {
    // Get the actual promo code
    const code = promo.code || promo.displayText?.match(/[A-Z0-9]{6,}/)?.[0] || promo.displayText;

    if (!code) {
      console.warn("No valid promo code found");
      return;
    }

    try {
      await navigator.clipboard.writeText(code);
      setToast({ message: `${code} copied!`, visible: true });

      // Auto-hide after 2.5 seconds
      setTimeout(() => {
        setToast((prev) => ({ ...prev, visible: false }));
      }, 2500);
    } catch (err) {
      console.error("Failed to copy:", err);
      setToast({ message: "Failed to copy", visible: true });
      setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), 2500);
    }
  };

  // Keyboard support
  const handleKeyDown = (e, promo) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handlePromoClick(promo);
    }
  };

  // Extended list for seamless loop
  const extendedPromoCodes = hasPromoCodes
    ? [...promoCodes, ...promoCodes, ...promoCodes, ...promoCodes, ...promoCodes, ...promoCodes]
    : [];

  // Toast component that will be rendered in a portal
  const ToastNotification = () => (
    <AnimatePresence>
      {toast?.visible && (
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          className="fixed top-4 right-4 z-[9999] bg-green-600 text-white px-5 py-3 rounded-lg shadow-xl flex items-center gap-3 pointer-events-auto"
          style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 9999
          }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="font-medium">{toast.message}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (promoCodesLoading) {
    return (
      <div className="relative w-full bg-[#076461] h-12 md:h-10 sm:h-8 border-b-2 border-[#FCBD01]/20 overflow-hidden">
        <div className="flex items-center justify-center h-full">
          <div className="text-white text-sm md:text-xs sm:text-xs font-medium animate-pulse">
            Loading offers...
          </div>
        </div>
      </div>
    );
  }

  if (promoCodesError) {
    console.warn("PromoRibbon error:", promoCodesError);
  }

  if (!hasPromoCodes) {
    console.warn("PromoRibbon: No promo codes available");
    return null;
  }

  return (
    <>
      {/* Toast Notification - Rendered in Portal to appear at top-right of entire page */}
      {isClient && typeof document !== 'undefined' && createPortal(
        <ToastNotification />,
        document.body
      )}

      {/* Main Ribbon */}
      <motion.section
        className="relative w-full bg-[#076461] h-12 md:h-10 sm:h-8 border-b-2 border-[#FCBD01]/20 overflow-hidden"
        aria-label="Promotional offers ribbon"
        role="banner"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {/* Decorative background patterns */}
        <div className="absolute inset-0 opacity-10" aria-hidden="true">
          <div className="absolute top-1 left-4 w-6 h-6 border border-white rounded-full max-md:w-4 max-md:h-4 max-sm:w-3 max-sm:h-3"></div>
          <div className="absolute top-2 right-8 w-4 h-4 border border-white rounded-full max-md:w-3 max-md:h-3 max-sm:w-2 max-sm:h-2"></div>
          <div className="absolute top-1 left-1/4 w-3 h-3 bg-white rounded-full max-md:w-2 max-md:h-2 max-sm:w-1 max-sm:h-1"></div>
          <div className="absolute top-2 right-1/3 w-8 h-8 border border-white rounded-full max-md:w-6 max-md:h-6 max-sm:w-4 max-sm:h-4"></div>
          <div className="absolute top-1 left-1/2 w-2 h-2 bg-white rounded-full max-sm:w-1 max-sm:h-1"></div>
          <div className="absolute top-2 right-1/4 w-3 h-3 border border-white transform rotate-45 max-md:w-2 max-md:h-2 max-sm:w-1 max-sm:h-1"></div>
          <div className="absolute top-1 left-3/4 w-5 h-5 border border-white transform rotate-45 max-md:w-3 max-md:h-3 max-sm:w-2 max-sm:h-2"></div>
        </div>

        {/* Left & Right edge decorations */}
        <div className="absolute left-0 top-0 h-full w-8 md:w-6 sm:w-4 flex items-center justify-center opacity-20" aria-hidden="true">
          <div className="flex items-center space-x-1 md:space-x-0.5 sm:space-x-0.5">
            <div className="w-2 h-2 md:w-1.5 md:h-1.5 sm:w-1 sm:h-1 border border-[#FCBD01] rounded-full"></div>
            <div className="w-1.5 h-1.5 md:w-1 md:h-1 sm:w-0.5 sm:h-0.5 bg-[#FCBD01] rounded-full"></div>
            <div className="w-2 h-2 md:w-1.5 md:h-1.5 sm:w-1 sm:h-1 border border-[#FCBD01] transform rotate-45"></div>
          </div>
        </div>

        <div className="absolute right-0 top-0 h-full w-8 md:w-6 sm:w-4 flex items-center justify-center opacity-20" aria-hidden="true">
          <div className="flex items-center space-x-1 md:space-x-0.5 sm:space-x-0.5">
            <div className="w-0 h-0 border-l-2 border-r-2 border-b-3 md:border-l-1 md:border-r-1 md:border-b-2 border-l-transparent border-r-transparent border-b-[#FCBD01]"></div>
            <div className="w-2 h-2 md:w-1.5 md:h-1.5 sm:w-1 sm:h-1 bg-[#FCBD01] transform rotate-45"></div>
            <div className="w-1.5 h-1.5 md:w-1 md:h-1 sm:w-0.5 sm:h-0.5 border border-[#FCBD01] rounded-full"></div>
          </div>
        </div>

        {/* Main sliding content */}
        <div
          className="flex items-center h-full overflow-hidden w-full"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          role="marquee"
          aria-live="polite"
          aria-label="Scrolling promotional offers"
        >
          <motion.div
            className="flex items-center whitespace-nowrap"
            style={{
              width: 'max-content',
              x: x
            }}
            animate={controls}
          >
            {extendedPromoCodes.map((promo, index) => {
              const promoData = typeof promo === 'string'
                ? { displayText: promo, code: null, product_id: null }
                : promo;

              // Prefer .code if available, fallback to extracting from displayText
              const displayCode = promoData.code || promoData.displayText;

              return (
                <React.Fragment key={`promo-${index}`}>
                  <span
                    className="text-white text-sm md:text-xs sm:text-xs font-medium hover:text-[#FCBD01] transition-colors duration-200 cursor-pointer px-4 md:px-3 sm:px-2 select-none"
                    role="button"
                    tabIndex="0"
                    aria-label={`Copy promo code: ${displayCode}`}
                    onClick={() => handlePromoClick(promoData)}
                    onKeyDown={(e) => handleKeyDown(e, promoData)}
                    title="Click to copy promo code"
                  >
                    {promoData.displayText} <span className="text-[#FCBD01] font-bold">COUPON</span>
                  </span>

                  {index !== extendedPromoCodes.length - 1 && (
                    <div className="w-px h-4 md:h-3 sm:h-2 bg-white/30 mx-6 md:mx-4 sm:mx-3" aria-hidden="true" />
                  )}
                </React.Fragment>
              );
            })}
          </motion.div>
        </div>

        {/* Gradient edges */}
        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#FCBD01]/40 to-transparent" aria-hidden="true"></div>
        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#FCBD01]/40 to-transparent" aria-hidden="true"></div>

        <div className="absolute left-0 top-0 h-full w-16 bg-gradient-to-r from-[#076461] to-transparent pointer-events-none" aria-hidden="true"></div>
        <div className="absolute right-0 top-0 h-full w-16 bg-gradient-to-l from-[#076461] to-transparent pointer-events-none" aria-hidden="true"></div>
      </motion.section>
    </>
  );
};

export default PromoRibbon;
